import { InputHTMLAttributes, forwardRef, KeyboardEvent, useId } from 'react';
import { moveFocusVertical, moveFocusHorizontal } from '@/shared/keynav/spatialNav';
import { DatePicker } from './DatePicker';
import { useField, fieldAria } from './FieldContext';

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  invalid?: boolean;
};

// Selection API is only legal on text-ish inputs; date/number/etc. throw.
function caretEdges(el: HTMLInputElement): { atStart: boolean; atEnd: boolean } {
  try {
    const s = el.selectionStart; const e = el.selectionEnd;
    if (s == null || e == null) return { atStart: false, atEnd: false };
    return { atStart: s === 0 && e === 0, atEnd: s === el.value.length && e === el.value.length };
  } catch { return { atStart: false, atEnd: false }; }
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, className = '', onKeyDown, ...rest },
  ref,
) {
  const field = useField();
  const token = useId();
  // RULE: every date field in the app uses the shared <DatePicker> (calendar
  // popover with Clear/Today), never the browser's native date control. Any
  // `<Input type="date">` therefore renders the picker — one place, so call
  // sites (and future ones) get it for free. The callers' event-shaped
  // onChange is adapted to the picker's (value: string) signature.
  if (rest.type === 'date') {
    const { value, onChange, disabled, readOnly, placeholder, 'aria-label': ariaLabel } = rest;
    return (
      <DatePicker
        value={value == null ? '' : String(value)}
        onChange={(v) => onChange?.({ target: { value: v } } as React.ChangeEvent<HTMLInputElement>)}
        disabled={disabled}
        readOnly={readOnly}
        {...(placeholder ? { placeholder: String(placeholder) } : {})}
        {...(ariaLabel ? { ariaLabel: String(ariaLabel) } : {})}
        {...(rest.id ? { id: rest.id } : {})}
        triggerClassName={className || undefined}
      />
    );
  }
  // Invalid by its own prop or by the field's error: red border + aria-invalid.
  const aria = fieldAria(field, rest, invalid, token);
  const isInvalid = aria['aria-invalid'] === true || aria['aria-invalid'] === 'true';
  // Arrow-key travel (same convention as the inline editors on the invoice
  // detail page): Up/Down move focus to the neighbouring field so the arrows
  // roam the whole form; Left/Right travel only when the caret sits at the
  // start/end of the value, so text editing keeps its arrows. A caller's
  // onKeyDown runs first and can preventDefault to opt out.
  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    onKeyDown?.(e);
    if (e.defaultPrevented) return;
    const el = e.currentTarget;
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      moveFocusVertical(el, e.key === 'ArrowUp' ? 'up' : 'down');
      return;
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      const { atStart, atEnd } = caretEdges(el);
      if ((e.key === 'ArrowLeft' && atStart) || (e.key === 'ArrowRight' && atEnd)) {
        e.preventDefault();
        moveFocusHorizontal(el, e.key === 'ArrowLeft' ? 'left' : 'right');
      }
    }
  }
  return (
    <input
      ref={ref}
      onKeyDown={handleKeyDown}
      className={`w-full h-[26px] px-2.5 rounded-md border bg-surface text-[0.8125rem] outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary transition-colors duration-150
        ${isInvalid
          ? 'border-red-500 focus:border-red-500'
          : 'border-controlBorder focus:border-primary'}
        ${className}`}
      {...rest}
      {...aria}
    />
  );
});
